// Thread breadcrumbs (fixed_header): which ancestor posts show as crumbs and
// which fold into a count badge, and which thread each crumb opens.
//
// A thread's ancestors are the posts above it, root first: each one heads (or
// branched off into) the next thread down. Short chains show every crumb;
// longer ones keep both ends, the root and the nearest parent, and fold the
// levels between them into "⋯ N" (channel › root › ⋯ N › parent).

export const MAX_INLINE_CRUMBS = 2

export type CrumbLayout = { folded: number[]; inline: number[] }

// Compact (phones): the channel and the nearest parent take precedence; every
// level above the parent, root included, folds into the badge.
export function layoutAncestorCrumbs(
    count: number,
    { compact = false }: { compact?: boolean } = {},
): CrumbLayout {
    if (count <= 0) return { folded: [], inline: [] }
    const all = Array.from({ length: count }, (_, i) => i)
    if (compact) return { folded: all.slice(0, -1), inline: [count - 1] }
    if (count <= MAX_INLINE_CRUMBS) return { folded: [], inline: all }
    return { folded: all.slice(1, -1), inline: [0, count - 1] }
}

type AnswerRef = {
    id?: string | number | null
    quest_id?: string | number | null
    // The thread this post opens (the read payload's ancestors carry it).
    branch_quest_id?: string | number | null
}

// The thread a crumb opens (the one that post heads), scrolled to the post
// that leads on down: the next ancestor, or for the nearest parent, the post
// this thread branched from.
export function ancestorCrumbTarget(
    ancestors: AnswerRef[],
    index: number,
    parentQuestId: string | null,
    parentAnswerId: string | number | null | undefined,
): { questId: string; answerId?: string } | null {
    const next = ancestors[index + 1]
    const leadsOn = next ? next.id : parentAnswerId
    const questId =
        ancestors[index]?.branch_quest_id ?? (next ? next.quest_id : parentQuestId) ?? null
    if (questId == null) return null
    return {
        questId: String(questId),
        answerId: leadsOn != null ? String(leadsOn) : undefined,
    }
}

// The first few words of a post, for a crumb.
export function crumbWords(text: string | null | undefined, maxWords = 4): string {
    const words = String(text || '')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .filter(Boolean)
    if (words.length === 0) return ''
    return words.length > maxWords ? `${words.slice(0, maxWords).join(' ')}…` : words.join(' ')
}
